/**
 * LINE rich menu management (Level 2) — lives under the booking-page panel,
 * next to branding, because that's what it consumes.
 *
 * Two modes:
 *   generated — one click, drawn server-side from logo + accent.
 *   custom    — merchant uploads designer artwork over a preset layout and
 *               assigns a whitelisted action per tap zone. The zone geometry
 *               comes from GET /layouts (the server's truth), so the preview
 *               overlay can never disagree with the provisioned hotspots.
 *
 * Client-side we pre-check image dimensions/size for fast feedback, but the
 * backend re-validates everything — these checks are UX, not enforcement.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Loader2, MessageSquare, Upload } from 'lucide-react'

import { api } from './api/client'
import type {
  RichMenuAction,
  RichMenuLayoutDef,
  RichMenuStatus,
} from './api/client'
import { ApiError } from './api/types'
import { Button } from './components/ui/button'
import { Card, CardContent } from './components/ui/card'
import { Input } from './components/ui/input'
import { Label } from './components/ui/label'
import { toast } from './components/ui/use-toast'

const PORTAL_TABS = ['status', 'report', 'contact', 'reservations', 'book', 'info']

function defaultActions(n: number): RichMenuAction[] {
  const tabs: RichMenuAction[] = PORTAL_TABS.map((tab) => ({ type: 'portal', tab }))
  return Array.from({ length: n }, (_, i) => tabs[i % tabs.length])
}

export default function RichMenuSection({
  brandingSavedAt,
}: {
  brandingSavedAt?: number | null
}) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<RichMenuStatus | null>(null)
  const [layouts, setLayouts] = useState<RichMenuLayoutDef[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)

  // custom-design working state
  const [mode, setMode] = useState<'generated' | 'custom'>('generated')
  const [layoutId, setLayoutId] = useState('grid6')
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [actions, setActions] = useState<RichMenuAction[]>(defaultActions(6))
  const [chatBar, setChatBar] = useState('選單')
  const fileRef = useRef<HTMLInputElement>(null)

  const layout = layouts.find((l) => l.id === layoutId)

  useEffect(() => {
    let alive = true
    Promise.all([api.richMenu.status(), api.richMenu.layouts()])
      .then(([s, l]) => {
        if (!alive) return
        setStatus(s)
        setLayouts(l.layouts)
        if (s.custom_config) {
          setMode(s.rich_menu_mode)
          setLayoutId(s.custom_config.layout)
          setImageUrl(s.custom_config.image_url)
          setActions(s.custom_config.actions)
          setChatBar(s.custom_config.chat_bar_text || '選單')
        }
      })
      .catch(() => alive && setStatus(null))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  function pickLayout(id: string) {
    setLayoutId(id)
    const def = layouts.find((l) => l.id === id)
    if (def) setActions((prev) => defaultActions(def.zones.length).map((d, i) => prev[i] ?? d))
    // A different layout means different dimensions — stale art won't fit.
    setImageUrl(null)
  }

  async function handleUpload(file: File | null) {
    if (!file || !layout) return
    if (file.size > 1024 * 1024) {
      toast({ variant: 'destructive', description: t('richMenu.tooBig') })
      return
    }
    // Fast local dimension check (backend re-validates).
    const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight })
      img.onerror = reject
      img.src = URL.createObjectURL(file)
    }).catch(() => null)
    if (!dims || dims.w !== layout.width || dims.h !== layout.height) {
      toast({
        variant: 'destructive',
        description: t('richMenu.wrongSize', {
          w: layout.width, h: layout.height,
          gw: dims?.w ?? '?', gh: dims?.h ?? '?',
        }),
      })
      return
    }
    setBusy('upload')
    try {
      const url = await api.uploadClinicAsset('rich_menu', file)
      setImageUrl(url)
    } catch (e) {
      toast({ variant: 'destructive', description: e instanceof ApiError ? e.message : String(e) })
    } finally {
      setBusy(null)
    }
  }

  async function downloadTemplate() {
    if (!layout) return
    setBusy('template')
    try {
      const blob = await api.richMenu.downloadTemplate(layout.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `petpro-rich-menu-${layout.id}-template.png`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setBusy(null)
    }
  }

  async function applyGenerated() {
    setBusy('generated')
    try {
      await api.richMenu.provision('generated')
      setStatus(await api.richMenu.status())
      toast({ description: t('richMenu.applied') })
    } catch (e) {
      toast({ variant: 'destructive', description: e instanceof ApiError ? e.message : String(e) })
    } finally {
      setBusy(null)
    }
  }

  async function saveAndApplyCustom() {
    if (!imageUrl) {
      toast({ variant: 'destructive', description: t('richMenu.needImage') })
      return
    }
    setBusy('custom')
    try {
      await api.richMenu.saveCustom({
        image_url: imageUrl,
        layout: layoutId,
        actions,
        chat_bar_text: chatBar || null,
      })
      await api.richMenu.provision('custom')
      setStatus(await api.richMenu.status())
      toast({ description: t('richMenu.applied') })
    } catch (e) {
      toast({ variant: 'destructive', description: e instanceof ApiError ? e.message : String(e) })
    } finally {
      setBusy(null)
    }
  }

  async function remove() {
    setBusy('remove')
    try {
      await api.richMenu.unprovision()
      setStatus(await api.richMenu.status())
      toast({ description: t('richMenu.removed') })
    } finally {
      setBusy(null)
    }
  }

  function setAction(i: number, patch: Partial<RichMenuAction>) {
    setActions((prev) => prev.map((a, j) => (j === i ? { ...a, ...patch } as RichMenuAction : a)))
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        <Loader2 className="mr-2 inline size-4 animate-spin" />
        {t('loading')}
      </div>
    )
  }

  return (
    <section className="space-y-3 pt-6">
      <div className="space-y-0.5">
        <h2 className="text-base font-semibold tracking-tight">
          <MessageSquare className="mr-1.5 inline size-4 -translate-y-px" />
          {t('richMenu.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('richMenu.sub')}</p>
      </div>

      {!status?.line_connected ? (
        <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          {t('richMenu.notConnected')}
        </div>
      ) : (
        <>
          {/* Current state */}
          <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm">
            {status.rich_menu_provisioned ? (
              <>
                <span className="font-medium">
                  {t('richMenu.statusProvisioned', {
                    when: status.provisioned_at?.slice(0, 10) ?? '—',
                  })}
                </span>{' '}
                <span className="text-muted-foreground">
                  ({t(`richMenu.statusMode_${status.rich_menu_mode}`)})
                </span>
                {brandingSavedAt && status.rich_menu_mode === 'generated' && (
                  <div className="mt-1 text-xs text-warning">{t('richMenu.brandingHint')}</div>
                )}
              </>
            ) : (
              <span className="text-muted-foreground">{t('richMenu.statusNone')}</span>
            )}
          </div>

          {/* Mode picker */}
          <div className="grid gap-3 sm:grid-cols-2">
            <Card
              className={`cursor-pointer ${mode === 'generated' ? 'border-primary' : ''}`}
              onClick={() => setMode('generated')}
            >
              <CardContent className="space-y-2 p-4">
                <div className="text-sm font-semibold">{t('richMenu.modeGenerated')}</div>
                <p className="text-xs text-muted-foreground">{t('richMenu.modeGeneratedHint')}</p>
                {mode === 'generated' && (
                  <Button size="sm" onClick={applyGenerated} disabled={busy !== null}>
                    {busy === 'generated' ? (
                      <>
                        <Loader2 className="size-3.5 animate-spin" /> {t('richMenu.applying')}
                      </>
                    ) : status.rich_menu_provisioned ? (
                      t('richMenu.reapply')
                    ) : (
                      t('richMenu.apply')
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
            <Card
              className={`cursor-pointer ${mode === 'custom' ? 'border-primary' : ''}`}
              onClick={() => setMode('custom')}
            >
              <CardContent className="space-y-2 p-4">
                <div className="text-sm font-semibold">{t('richMenu.modeCustom')}</div>
                <p className="text-xs text-muted-foreground">{t('richMenu.modeCustomHint')}</p>
              </CardContent>
            </Card>
          </div>

          {/* Custom designer flow */}
          {mode === 'custom' && layout && (
            <div className="space-y-4 rounded-lg border border-border bg-card p-4">
              {/* Layout picker as mini zone diagrams */}
              <div className="space-y-1.5">
                <Label>{t('richMenu.layout')}</Label>
                <div className="flex flex-wrap gap-2">
                  {layouts.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => pickLayout(l.id)}
                      className={`rounded-md border p-1.5 ${
                        l.id === layoutId ? 'border-primary ring-1 ring-primary' : 'border-border'
                      }`}
                      title={l.id}
                    >
                      <div
                        className="relative bg-muted"
                        style={{ width: 72, height: (72 * l.height) / l.width }}
                      >
                        {l.zones.map((z, i) => (
                          <div
                            key={i}
                            className="absolute border border-background bg-muted-foreground/20"
                            style={{
                              left: `${(z.x / l.width) * 100}%`,
                              top: `${(z.y / l.height) * 100}%`,
                              width: `${(z.width / l.width) * 100}%`,
                              height: `${(z.height / l.height) * 100}%`,
                            }}
                          />
                        ))}
                      </div>
                      <div className="mt-0.5 text-center text-[10px] text-muted-foreground">
                        {l.id}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Template + upload */}
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={downloadTemplate} disabled={busy !== null}>
                  <Download className="size-3.5" /> {t('richMenu.template')}
                </Button>
                <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy !== null}>
                  {busy === 'upload' ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" /> {t('richMenu.uploading')}
                    </>
                  ) : (
                    <>
                      <Upload className="size-3.5" />{' '}
                      {imageUrl ? t('richMenu.replace') : t('richMenu.upload')}
                    </>
                  )}
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => handleUpload(e.target.files?.[0] ?? null)}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('richMenu.templateHint', { w: layout.width, h: layout.height })}
              </p>

              {/* Preview with zone overlay */}
              {imageUrl && (
                <div className="space-y-1.5">
                  <div
                    className="relative w-full overflow-hidden rounded-md border border-border"
                    style={{ aspectRatio: `${layout.width} / ${layout.height}` }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
                    {layout.zones.map((z, i) => (
                      <div
                        key={i}
                        className="absolute flex items-start justify-start border-2 border-dashed border-white/70"
                        style={{
                          left: `${(z.x / layout.width) * 100}%`,
                          top: `${(z.y / layout.height) * 100}%`,
                          width: `${(z.width / layout.width) * 100}%`,
                          height: `${(z.height / layout.height) * 100}%`,
                        }}
                      >
                        <span className="m-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                          {i + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">{t('richMenu.previewHint')}</p>
                </div>
              )}

              {/* Per-zone actions */}
              <div className="space-y-1.5">
                <Label>{t('richMenu.zoneActions')}</Label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {layout.zones.map((_z, i) => {
                    const a = actions[i] ?? { type: 'portal', tab: 'status' }
                    return (
                      <div key={i} className="flex items-center gap-2 rounded-md border border-border p-2">
                        <span className="w-14 shrink-0 text-xs text-muted-foreground">
                          {t('richMenu.zone', { n: i + 1 })}
                        </span>
                        <select
                          className="h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-xs"
                          value={a.type === 'portal' ? `portal:${a.tab}` : a.type}
                          onChange={(e) => {
                            const v = e.target.value
                            if (v.startsWith('portal:')) setAction(i, { type: 'portal', tab: v.slice(7), url: undefined })
                            else if (v === 'chat') setAction(i, { type: 'chat', tab: undefined, url: undefined })
                            else setAction(i, { type: 'url', tab: undefined, url: a.url ?? '' })
                          }}
                        >
                          {PORTAL_TABS.map((tab) => (
                            <option key={tab} value={`portal:${tab}`}>
                              {t(`richMenu.tab_${tab}`)}
                            </option>
                          ))}
                          <option value="chat">{t('richMenu.actionChat')}</option>
                          <option value="url">{t('richMenu.actionUrl')}</option>
                        </select>
                        {a.type === 'url' && (
                          <Input
                            className="h-8 flex-1 text-xs"
                            placeholder={t('richMenu.urlPlaceholder')}
                            value={a.url ?? ''}
                            onChange={(e) => setAction(i, { url: e.target.value })}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Chat bar text */}
              <div className="max-w-xs space-y-1.5">
                <Label htmlFor="chatbar">{t('richMenu.chatBar')}</Label>
                <Input
                  id="chatbar"
                  maxLength={14}
                  value={chatBar}
                  onChange={(e) => setChatBar(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">{t('richMenu.chatBarHint')}</p>
              </div>

              <Button onClick={saveAndApplyCustom} disabled={busy !== null}>
                {busy === 'custom' ? (
                  <>
                    <Loader2 className="size-4 animate-spin" /> {t('richMenu.applying')}
                  </>
                ) : (
                  t('richMenu.applyCustom')
                )}
              </Button>
            </div>
          )}

          {/* Remove */}
          {status.rich_menu_provisioned && (
            <Button variant="outline" size="sm" onClick={remove} disabled={busy !== null}>
              {t('richMenu.remove')}
            </Button>
          )}
        </>
      )}
    </section>
  )
}
